/**
 * Canonical news dataset for Fenix Srl.
 *
 * Each article describes a single news post: identity, publication date,
 * imagery and optional tags. User-facing copy (title, excerpt, body) is
 * referenced via translation keys — see `newsData.items.*` in
 * `public/locales/{lang}/common.json`.
 *
 * Slugs are kebab-case and drive the URL segments under
 * `/it/news/:slug` and `/en/news/:slug` (Italian and English share the
 * same `news` URL segment by design).
 */

export interface NewsArticle {
  /** URL slug — kebab-case (e.g. `global-industrie-2023`) */
  slug: string
  /**
   * Translation key suffix used under `newsData.items.{translationKey}.{title,excerpt,body,alt}`.
   * Kept explicit (instead of derived from the slug) so slug and i18n key can evolve
   * independently.
   */
  translationKey: string
  /** ISO 8601 publication date (e.g. `2023-03-08`) */
  date: string
  /** Primary image — absolute public path */
  image: string
  /** Optional tags — translation keys under `newsData.tags.*` */
  tagKeys?: string[]
}

export const news: NewsArticle[] = [
  {
    slug: 'global-industrie-2023',
    translationKey: 'globalIndustrie2023',
    date: '2023-03-07',
    image: '/images/news/global-industrie-2023.jpg',
    tagKeys: ['tradeShow', 'international'],
  },
  {
    slug: 'mecspe-2023',
    translationKey: 'mecspe2023',
    date: '2023-03-06',
    image: '/images/news/mecspe-2023.jpg',
    tagKeys: ['tradeShow', 'italy'],
  },
  {
    slug: 'mecspe-2021',
    translationKey: 'mecspe2021',
    date: '2021-11-20',
    image: '/images/news/mecspe-2021.jpg',
    tagKeys: ['tradeShow', 'italy'],
  },
  {
    slug: 'australia-rettificatrice-tangenziale',
    translationKey: 'australiaGrinder',
    date: '2023-08-25',
    image: '/images/news/australia-rettificatrice.jpg',
    tagKeys: ['installation', 'international'],
  },
  {
    slug: 'rettifica-meccanica-principi',
    translationKey: 'rettificaMeccanica',
    date: '2024-09-12',
    image: '/images/news/rettifica-meccanica.jpg',
    tagKeys: ['technical', 'insight'],
  },
  {
    slug: 'fenix-siemens-sinergia',
    translationKey: 'fenixSiemens',
    date: '2024-06-21',
    image: '/images/news/fenix-siemens.jpg',
    tagKeys: ['technology', 'partnership'],
  },
]

/**
 * Returns the fully qualified i18n key for a given article field.
 * e.g. `newsKey(article, 'title')` → `newsData.items.globalIndustrie2023.title`
 */
export function newsKey(
  article: NewsArticle,
  field: 'title' | 'excerpt' | 'body' | 'alt'
): string {
  return `newsData.items.${article.translationKey}.${field}`
}

/**
 * Lookup an article by its URL slug. Returns `undefined` if no match.
 */
export function getNewsBySlug(slug: string): NewsArticle | undefined {
  return news.find((n) => n.slug === slug)
}

/**
 * Articles sorted newest-first by ISO date (stable on ties).
 */
export function getNewsSortedByDate(): NewsArticle[] {
  return [...news].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
}

/**
 * Returns up to `limit` related articles (excluding the current slug),
 * sorted newest-first.
 */
export function getRelatedNews(current: NewsArticle, limit = 2): NewsArticle[] {
  return getNewsSortedByDate()
    .filter((n) => n.slug !== current.slug)
    .slice(0, limit)
}

/**
 * Convenience map from tag key to a Tailwind accent treatment. Kept in the
 * data file so the listing and detail pages render tags consistently.
 */
export const NEWS_TAG_KEYS = [
  'tradeShow',
  'italy',
  'international',
  'installation',
  'technical',
  'insight',
  'technology',
  'partnership',
] as const
export type NewsTagKey = (typeof NEWS_TAG_KEYS)[number]
